/**
 * Manages the state and interactions for an Inside Chat integration.
 *
 * @function useInsideChat
 * @returns {Object} An object containing Inside Chat state and interaction method.
 *
 * @description
 * This composable provides a reactive state management solution for Inside Chat functionality.
 * It tracks the chat's availability, loading state, and provides a method to open the chat pane.
 *
 * @property {Ref<Object>} insideChat - Reactive state of the Inside Chat.
 * @property {boolean} insideChat.isAvailable - Indicates if the chat is currently available.
 * @property {boolean} insideChat.isLoaded - Indicates if the chat has been loaded.
 * @property {boolean} insideChat.hasBeenRequestedBeforeInit - Tracks if chat was requested before initialization.
 * @property {Function} openInsideChat - Method to open the chat pane.
 *
 * @example
 * // Using the Inside Chat composable
 * const { insideChat, openInsideChat } = useInsideChat()
 *
 * // Open chat if available
 * openInsideChat()
 *
 * // Check chat state
 * console.log(insideChat.value.isAvailable)
 *
 * @note
 * - Handles potential errors during chat opening
 * - Supports deferred chat initialization
 * - Uses type-unsafe window access for cross-browser compatibility
 */
export function useInsideChat() {
  const insideChat = useState<{
    isAvailable: boolean
    isLoaded: boolean
    hasBeenRequestedBeforeInit: boolean
  }>(() => ({
    isAvailable: false,
    isLoaded: false,
    hasBeenRequestedBeforeInit: false,
  }))

  const openInsideChat = () => {
    try {
      if (insideChat.value.isLoaded) {
        // @ts-ignore
        window?.insideFrontInterface?.openChatPane()
      } else insideChat.value.hasBeenRequestedBeforeInit = true
    } catch (error) {
      console.error('Error in openInsideChat() : ', error)
    }
  }

  return {
    insideChat,
    openInsideChat,
  }
}
